import React, { useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Tag, message } from 'antd';
import classNames from 'classnames';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSharedValidations } from '../../../../hooks/useSharedValidations';

import Label from '../../../atoms/Label';
import InfoText from '../../../atoms/InfoText';
import Button from '../../../atoms/Button';
import FormField from '../../../FormField/FormField';
import EmptyState from '../../EmptyState';

import {
  useAddUserCategoryMutation,
  useGetCategoriesLkQuery,
  useGetCategorySpecialtiesLkQuery,
} from '../../../../store/services/sellerProfileCreationService';

export default function SpecialtyForm({
  profileId,
  initialData,
  onSuccess,
}: {
  profileId: string;
  initialData?: any;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();

  const [addCategory, { isLoading }] = useAddUserCategoryMutation();
  const { data: categoriesLk, isLoading: catLoading } = useGetCategoriesLkQuery({});


  const { zodRequiredStringValidate } = useSharedValidations();

  const schema = z.object({
    industry: zodRequiredStringValidate,
    specialty: z.array(z.string()).min(1, { message: t('components.validations.requiredField') }),
  });

  const methods = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      industry: initialData?.id?.toString() || undefined,
      specialty: initialData?.specialties?.map((s: any) => s.id) || ([] as string[]),
    },
  });

  const { watch, setValue, handleSubmit, formState: { errors } } = methods;
  const selectedIndustry = watch('industry');
  const selectedSpecialty = watch('specialty');
  
  const { data: specialtiesLk, isLoading: specLoading } = useGetCategorySpecialtiesLkQuery(
    { categoryId: selectedIndustry },
    { skip: !selectedIndustry }
  );

  const handleTagClick = (value: string) => {
    const current = selectedSpecialty || [];
    const exists = current.includes(value);
    const next = exists ? current.filter((v: string) => v !== value) : [...current, value];
    setValue('specialty', next, { shouldValidate: true });
  };

  const onSubmit = async (data: any) => {
    try {
      // API expects an array of IDs, send them all at once
      await addCategory({ profileId, ids: data.specialty }).unwrap();
      onSuccess();
    } catch (err) {
      message.error(t('components.somethingWentWrong'));
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} data-testid="profile-creation-specialty-form">
        <div className="space-y-6">
          <FormField
            type="select"
            id="industry"
            name="industry"
            label={t('components.select industry')}
            placeholder={t('components.select industry')}
            options={categoriesLk?.payload?.map((i: any) => ({
              label: i.name,
              value: i.id?.toString(),
            })) || []}
            onChange={(val: any) => {
              setValue('industry', val);
              setValue('specialty', []);
            }}
          />

          <div className="space-y-1">
            <Label text={t('components.specialty')} />
            <div className="flex items-center gap-2 flex-wrap">
              {specLoading ? (
                <p>Loading...</p>
              ) : specialtiesLk?.payload?.length ? (
                specialtiesLk.payload.map((option: any) => {
                  const isSelected = selectedSpecialty?.includes(option.id?.toString());
                  return (
                    <Tag
                      key={option.id}
                      className={classNames(
                        'w-fit py-2 px-4 rounded-lg border-light-input-border cursor-pointer flex gap-2 duration-200 transition-all text-sm',
                        isSelected ? 'bg-[rgb(83,164,108)] !text-white' : 'bg-gray-100 dark:bg-black-800'
                      )}
                      onClick={() => handleTagClick(option.id?.toString())}
                    >
                      {option.name}
                    </Tag>
                  );
                })
              ) : (
                <EmptyState onAdd={() => { }} />
              )}
            </div>
            {errors.specialty && (
              <InfoText size="xs" className="!text-red-500" text={errors.specialty.message as string} />
            )}
          </div>

          <div className="flex justify-end mt-4">
            <Button type="submit" disabled={isLoading}>
              {t('components.done')}
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
}
